import { FilterFilled, SearchOutlined } from "@ant-design/icons";
import { Collapse, Typography } from "antd";
import { useEffect, useState, useContext } from "react";
import CustomFormItemsHorizontal from "../common/CustomFormItemsHorizontal";
import useFetch from "../../hooks/useFetch";
import { IndexContext } from "../../context/IndexContext";
import dayjs from "dayjs";

const { Text } = Typography;

export default function ParameterTrendFilter({
  form,
  applyFilter,
  resetFilter,
}) {
  const [activeKey, setActiveKey] = useState(1);

  const [fetchWrapper] = useFetch();

  // loading for device list selection (load while it's still getting device list from backend)
  const [loadingDeviceList, setLoadingDeviceList] = useState(false);
  // loading for parameter list selection (load while it's still getting parameter list from backend)
  const [loadingParameterList, setLoadingParameterList] = useState(false);

  // device selection
  const [deviceList, setDeviceList] = useState([]);
  const [deviceSelected, setDeviceSelected] = useState(null);

  // parameter selection
  const [parameterList, setParameterList] = useState([]);
  const [parameterSelected, setParameterSelected] = useState(null);

  // site commissioning date
  const [siteCommissioningDate, setSiteCommissioningDate] = useState(dayjs());

  // load index context
  const { siteSelected } = useContext(IndexContext);

  /* API to get device list */
  function getDeviceList() {
    setDeviceList([]);
    setLoadingDeviceList(true);
    fetchWrapper({
      endpoint_url: "trending/getDeviceList",
      body: {
        site_id: siteSelected,
        device_view_id: 3,
      },
      onSuccess: (fetch_output) => {
        if (fetch_output.status === true) {
          setDeviceList(fetch_output.device_list);
        }
        setLoadingDeviceList(false);
      },
    });
  }

  /* API to get parameter list */
  function getParameterList() {
    setParameterList([]);
    setLoadingParameterList(true);
    fetchWrapper({
      endpoint_url: "trending/getParameterList",
      onSuccess: (fetch_output) => {
        if (fetch_output.status === true) {
          setParameterList(fetch_output.parameter_list);
        }
        setLoadingParameterList(false);
      },
    });
  }

  /* API to get site commissioning date */
  function getSiteCommissioningDate() {
    fetchWrapper({
      endpoint_url: "trending/getSiteCommissioningDate",
      body: {
        site_id: siteSelected,
      },
      onSuccess: (fetch_output) => {
        if (fetch_output.status === true) {
          setSiteCommissioningDate(fetch_output.site_commissioning_date);
        }
      },
    });
  }

  useEffect(() => {
    if (siteSelected) {
      getDeviceList();
      getSiteCommissioningDate();
    }
  }, [siteSelected]);

  useEffect(() => {
    getParameterList();
  }, []);

  useEffect(() => {
    form.setFieldValue("start", null);
    form.setFieldValue("end", null);

    setDeviceSelected(null);
    form.setFieldValue("device", []);

    setParameterSelected(null);
    form.setFieldValue("parameter", []);
  }, [siteSelected]);

  const items = [
    {
      key: 1,
      label: (
        <>
          <FilterFilled />
          <Text strong className="ml-2">
            FILTER
          </Text>
        </>
      ),
      children: (
        <>
          <CustomFormItemsHorizontal
            submitIcon={<SearchOutlined />}
            submitText={"Search"}
            resetFilter={resetFilter}
            onFinish={applyFilter}
            form={form}
            config={[
              {
                name: "start",
                label: "From",
                type: "datetime",
                minDate: dayjs(siteCommissioningDate, "YYYY-MM-DD"),
                maxDate: dayjs(),
                rules: [
                  {
                    required: true,
                    message: "Please select start date and time.",
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (getFieldValue("end")) {
                        if (getFieldValue("end") < value) {
                          return Promise.reject(
                            new Error(
                              "Start date must be same as or earlier than end date."
                            )
                          );
                        } else {
                          return Promise.resolve();
                        }
                      }
                    },
                  }),
                ],
              },
              {
                name: "end",
                label: "To",
                type: "datetime",
                minDate: dayjs(siteCommissioningDate, "YYYY-MM-DD"),
                maxDate: dayjs(),
                rules: [
                  {
                    required: true,
                    message: "Please select end date and time.",
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (getFieldValue("start")) {
                        if (getFieldValue("start") > value) {
                          return Promise.reject(
                            new Error(
                              "End date must be same as or later than start date."
                            )
                          );
                        } else {
                          return Promise.resolve();
                        }
                      }
                    },
                  }),
                ],
              },
              {
                name: "device",
                label: "Device",
                type: "tree",
                multiple: true,

                onChange: (value) => {
                  setDeviceSelected(value);
                },
                rules: [{ required: true, message: "Please select device." }],
                showCheckedStrategy: "all",
                options: deviceList,
                disabled: siteSelected ? false : true,
                loading: loadingDeviceList,
              },
              {
                name: "parameter",
                label: "Parameter",
                type: "select",
                multiple: true,
                onChange: (value) => {
                  setParameterSelected(value);
                },
                rules: [
                  { required: true, message: "Please select patameter." },
                ],
                showCheckedStrategy: "all",
                options: parameterList,
                disabled: siteSelected && deviceSelected ? false : true,
                loading: loadingParameterList,
              },
            ]}
          />
        </>
      ),
    },
  ];

  return (
    <Collapse
      expandIconPosition="end"
      defaultActiveKey={activeKey}
      size="middle"
      onChange={(key) => setActiveKey(key)}
      items={items}
    />
  );
}
